/**
 * PHSIMS Web Admin - Two-Step Verification (MFA) Setup
 * Author: Iswe Solutions Team
 *
 * Self-service enrolment and management of a second factor. Two methods:
 *   - Authenticator app (TOTP): the user adds a key to Google/Microsoft
 *     Authenticator, FreeOTP or 1Password and confirms a rolling code.
 *   - Email code: a 6-digit code is emailed at each sign-in.
 *
 * Enrolment is a two-step handshake (enroll -> confirm) so a mistake never
 * locks the user out. Matches the PHSIMS login page design.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Alert,
  Divider,
} from '@mui/material';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import KeyIcon from '@mui/icons-material/Key';
import SmartphoneIcon from '@mui/icons-material/Smartphone';
import MarkEmailReadOutlinedIcon from '@mui/icons-material/MarkEmailReadOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { apiClient } from '../services/api';
import { AppFooter } from './common/AppFooter';

type Method = 'totp' | 'email';
type Stage = 'loading' | 'choose' | 'totp' | 'email' | 'manage' | 'disable' | 'done';

interface StatusResp {
  mfa_enabled: boolean;
  method: Method | null;
  enrollment_required: boolean;
  enforced_for_role: boolean;
}

const FIELD_SX = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    backgroundColor: '#f8fafc',
    minHeight: '52px',
    '& fieldset': { borderWidth: '2px', borderColor: '#e2e8f0' },
    '&:hover fieldset': { borderColor: '#198a4b' },
    '&.Mui-focused fieldset': { borderColor: '#198a4b' },
    '&.Mui-focused': { backgroundColor: 'white', boxShadow: '0 0 0 3px rgba(25, 138, 75, 0.1)' },
  },
} as const;

const PRIMARY_BTN_SX = {
  py: 1.2,
  borderRadius: '10px',
  fontSize: '14px',
  fontWeight: 700,
  textTransform: 'none',
  background: 'linear-gradient(135deg, #198a4b 0%, #14703d 100%)',
  boxShadow: '0 4px 20px rgba(25, 138, 75, 0.35)',
  transition: 'all 0.3s',
  '&:hover': {
    background: 'linear-gradient(135deg, #ef7d00 0%, #f59e0b 100%)',
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 30px rgba(239, 125, 0, 0.4)',
  },
  '&:disabled': { background: '#cbd5e1', boxShadow: 'none', transform: 'none' },
} as const;

// Module-level so their identity is stable across renders — a component defined
// inside MfaSetupPage would remount its inputs on every keystroke (focus loss).
const Shell: React.FC<{ error: string; info: string; children: React.ReactNode }> = ({ error, info, children }) => (
  <Box sx={{ minHeight: '100vh', width: '100vw', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'auto', background: '#0d3322', position: 'fixed', top: 0, left: 0 }}>
    <Box sx={{ background: 'white', padding: { xs: '24px 20px', sm: '32px 40px' }, borderRadius: { xs: '16px', sm: '20px' }, boxShadow: '0 25px 80px rgba(0,0,0,0.5)', width: { xs: '100%', sm: 480 }, maxWidth: { xs: '94%', sm: '94%' }, borderTop: '5px solid #ef7d00', my: 4 }}>
      <Box sx={{ textAlign: 'center', mb: 2 }}>
        <Box component="img" src="/znphi_logo.jpg" alt="ZNPHI Logo" sx={{ width: { xs: 56, sm: 64 }, height: 'auto', borderRadius: '8px', mb: 1, objectFit: 'contain' }} />
        <Typography sx={{ color: '#ef7d00', fontSize: { xs: '20px', sm: '24px' }, fontWeight: 800, letterSpacing: '2px' }}>PHSIMS</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>Two-step verification</Typography>
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: '10px', fontSize: '13px', py: 0.5 }}>{error}</Alert>}
      {info && <Alert severity="info" sx={{ mb: 2, borderRadius: '10px', fontSize: '13px', py: 0.5 }}>{info}</Alert>}
      {children}
      <AppFooter variant="auth" />
    </Box>
  </Box>
);

const CodeForm: React.FC<{
  code: string;
  setCode: (v: string) => void;
  loading: boolean;
  onSubmit: (e: React.FormEvent) => void;
  label: string;
}> = ({ code, setCode, loading, onSubmit, label }) => (
  <form onSubmit={onSubmit}>
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75 }}>
      <TextField
        placeholder="000000" fullWidth required autoFocus value={code}
        onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
        disabled={loading}
        inputProps={{ inputMode: 'numeric', autoComplete: 'one-time-code', maxLength: 6, style: { textAlign: 'center', letterSpacing: '10px', fontSize: '24px', fontWeight: 700 } }}
        InputProps={{ startAdornment: <KeyIcon sx={{ color: '#198a4b', fontSize: 20, mr: 1 }} /> }}
        sx={FIELD_SX}
      />
      <Button type="submit" variant="contained" fullWidth disabled={loading || code.length < 6} sx={PRIMARY_BTN_SX}>
        {loading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : label}
      </Button>
    </Box>
  </form>
);

const MfaSetupPage: React.FC = () => {
  const navigate = useNavigate();

  const [stage, setStage] = useState<Stage>('loading');
  const [enrolledMethod, setEnrolledMethod] = useState<Method | null>(null);
  const [enforced, setEnforced] = useState(false);
  const [required, setRequired] = useState(false);

  const [secret, setSecret] = useState('');          // TOTP setup key
  const [maskedEmail, setMaskedEmail] = useState(''); // email destination
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [copied, setCopied] = useState(false);

  const loadStatus = useCallback(async () => {
    try {
      const resp = await apiClient.get<StatusResp>('/auth/mfa/status');
      const s = resp.data;
      setEnforced(s.enforced_for_role);
      setRequired(s.enrollment_required);
      if (s.mfa_enabled) {
        setEnrolledMethod(s.method);
        setStage('manage');
      } else {
        setStage('choose');
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Could not load your security settings.');
      setStage('choose');
    }
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  // After enrolment succeeds, briefly confirm then send the user into the app —
  // no manual "continue" click.
  useEffect(() => {
    if (stage !== 'done') return;
    const t = window.setTimeout(() => navigate('/'), 1800);
    return () => window.clearTimeout(t);
  }, [stage, navigate]);

  const beginTotp = async () => {
    setLoading(true); setError(''); setInfo(''); setCode('');
    try {
      const resp = await apiClient.post<{ secret: string }>('/auth/mfa/enroll', { method: 'totp' });
      setSecret(resp.data.secret);
      setStage('totp');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Could not start setup.');
    } finally { setLoading(false); }
  };

  const beginEmail = async () => {
    setLoading(true); setError(''); setInfo(''); setCode('');
    try {
      const resp = await apiClient.post<{ email: string }>('/auth/mfa/enroll', { method: 'email' });
      setMaskedEmail(resp.data.email);
      setInfo(`We sent a 6-digit code to ${resp.data.email}.`);
      setStage('email');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Could not send a code to your email.');
    } finally { setLoading(false); }
  };

  const resendEmail = async () => {
    setError(''); setInfo('');
    try {
      const resp = await apiClient.post<{ email: string }>('/auth/mfa/email/send', {});
      setInfo(`A new code is on its way to ${resp.data.email}.`);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Could not resend the code.');
    }
  };

  const confirmEnrol = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      await apiClient.post('/auth/mfa/confirm', { code });
      setStage('done');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'That code is not valid. Please try again.');
    } finally { setLoading(false); }
  };

  const beginDisable = async () => {
    setError(''); setInfo(''); setCode('');
    if (enrolledMethod === 'email') {
      try {
        const resp = await apiClient.post<{ email: string }>('/auth/mfa/email/send', {});
        setInfo(`We sent a confirmation code to ${resp.data.email}.`);
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : 'Could not send a confirmation code.');
      }
    }
    setStage('disable');
  };

  const confirmDisable = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      await apiClient.post('/auth/mfa/disable', { code });
      setEnrolledMethod(null);
      setInfo('Two-step verification has been turned off.');
      setCode('');
      setStage('choose');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'That code is not valid. Please try again.');
    } finally { setLoading(false); }
  };

  const copySecret = async () => {
    try { await navigator.clipboard.writeText(secret); setCopied(true); setTimeout(() => setCopied(false), 2000); }
    catch { /* clipboard blocked; the key is visible for manual entry */ }
  };

  if (stage === 'loading') {
    return <Shell error={error} info={info}><Box sx={{ textAlign: 'center', py: 3 }}><CircularProgress sx={{ color: '#198a4b' }} /></Box></Shell>;
  }

  if (stage === 'done') {
    return (
      <Shell error={error} info={info}>
        <Box sx={{ textAlign: 'center' }}>
          <CheckCircleIcon sx={{ fontSize: 64, color: '#00b894', mb: 1 }} />
          <Typography variant="h6" fontWeight={800} sx={{ color: '#0d5c2e', mb: 0.5 }}>Code accepted</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Two-step verification is on. Taking you to PHSIMS&hellip;
          </Typography>
          <CircularProgress size={22} sx={{ color: '#198a4b' }} />
        </Box>
      </Shell>
    );
  }

  if (stage === 'choose') {
    return (
      <Shell error={error} info={info}>
        {!enrolledMethod && (enforced || required) && (
          <Alert severity="warning" sx={{ mb: 2, borderRadius: '10px', fontSize: '13px' }}>
            Your role requires two-step verification. Please set up a method to continue.
          </Alert>
        )}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'center' }}>
          {enrolledMethod
            ? 'Pick how you’d like to receive your sign-in code, then confirm a code to switch.'
            : 'Add a second step at sign-in to keep your account secure. Choose how you’d like to receive your code.'}
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Button onClick={beginTotp} disabled={loading} startIcon={<SmartphoneIcon />} variant="contained" fullWidth sx={PRIMARY_BTN_SX}>
            Use an authenticator app
          </Button>
          <Button onClick={beginEmail} disabled={loading} startIcon={<MarkEmailReadOutlinedIcon />} variant="outlined" fullWidth
            sx={{ py: 1.2, borderRadius: '10px', fontWeight: 700, textTransform: 'none', borderWidth: '2px', borderColor: '#198a4b', color: '#198a4b', '&:hover': { borderWidth: '2px', borderColor: '#ef7d00', color: '#ef7d00', background: 'rgba(239,125,0,0.06)' } }}>
            Email me a code
          </Button>
        </Box>
        <Box sx={{ textAlign: 'center', mt: 2 }}>
          {enrolledMethod ? (
            <Button onClick={() => { setStage('manage'); setError(''); setInfo(''); }} startIcon={<ArrowBackIcon sx={{ fontSize: 16 }} />} sx={{ color: '#64748b', fontSize: '13px', textTransform: 'none' }}>
              Keep my current method
            </Button>
          ) : (!enforced && !required) ? (
            <Button onClick={() => navigate('/')} startIcon={<ArrowBackIcon sx={{ fontSize: 16 }} />} sx={{ color: '#64748b', fontSize: '13px', textTransform: 'none' }}>
              Back to PHSIMS
            </Button>
          ) : null}
        </Box>
      </Shell>
    );
  }

  if (stage === 'totp') {
    return (
      <Shell error={error} info={info}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          In Google Authenticator, Microsoft Authenticator, FreeOTP or 1Password, add an account and enter this setup key:
        </Typography>
        <Box onClick={copySecret}
          sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, bgcolor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '10px', px: 2, py: 1.5, mb: 0.5 }}>
          <Typography sx={{ fontFamily: 'monospace', fontSize: '16px', fontWeight: 700, letterSpacing: '2px', color: '#166534', wordBreak: 'break-all' }}>
            {secret.replace(/(.{4})/g, '$1 ').trim()}
          </Typography>
          <ContentCopyIcon sx={{ fontSize: 18, color: '#198a4b' }} />
        </Box>
        <Typography variant="caption" sx={{ color: copied ? '#00b894' : '#94a3b8', display: 'block', mb: 2 }}>
          {copied ? 'Copied to clipboard' : 'Tap the key to copy. It is entered once, at setup.'}
        </Typography>
        <Divider sx={{ mb: 2 }}>then</Divider>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Enter the 6-digit code your app now shows to finish:
        </Typography>
        <CodeForm code={code} setCode={setCode} loading={loading} onSubmit={confirmEnrol} label="Turn on two-step verification" />
        <Box sx={{ textAlign: 'center', mt: 1 }}>
          <Button onClick={() => { setStage('choose'); setError(''); setInfo(''); }} startIcon={<ArrowBackIcon sx={{ fontSize: 16 }} />} sx={{ color: '#64748b', fontSize: '13px', textTransform: 'none' }}>
            Choose a different method
          </Button>
        </Box>
      </Shell>
    );
  }

  if (stage === 'email') {
    return (
      <Shell error={error} info={info}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'center' }}>
          Enter the 6-digit code we sent to <strong>{maskedEmail}</strong> to finish.
        </Typography>
        <CodeForm code={code} setCode={setCode} loading={loading} onSubmit={confirmEnrol} label="Turn on two-step verification" />
        <Box sx={{ textAlign: 'center', mt: 1.5, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Button onClick={resendEmail} sx={{ color: '#198a4b', fontSize: '13px', fontWeight: 600, textTransform: 'none' }}>
            Didn&apos;t get it? Resend code
          </Button>
          <Button onClick={() => { setStage('choose'); setError(''); setInfo(''); }} startIcon={<ArrowBackIcon sx={{ fontSize: 16 }} />} sx={{ color: '#64748b', fontSize: '13px', textTransform: 'none' }}>
            Choose a different method
          </Button>
        </Box>
      </Shell>
    );
  }

  if (stage === 'disable') {
    return (
      <Shell error={error} info={info}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'center' }}>
          {enrolledMethod === 'email'
            ? 'Enter the confirmation code we just emailed you to turn off two-step verification.'
            : 'Enter a current code from your authenticator app to turn off two-step verification.'}
        </Typography>
        <CodeForm code={code} setCode={setCode} loading={loading} onSubmit={confirmDisable} label="Turn off two-step verification" />
        <Box sx={{ textAlign: 'center', mt: 1 }}>
          <Button onClick={() => { setStage('manage'); setError(''); setInfo(''); setCode(''); }} startIcon={<ArrowBackIcon sx={{ fontSize: 16 }} />} sx={{ color: '#64748b', fontSize: '13px', textTransform: 'none' }}>
            Keep it on
          </Button>
        </Box>
      </Shell>
    );
  }

  // stage === 'manage'
  return (
    <Shell error={error} info={info}>
      <Box sx={{ textAlign: 'center', mb: 2 }}>
        <ShieldOutlinedIcon sx={{ fontSize: 48, color: '#00b894' }} />
        <Typography variant="h6" fontWeight={800} sx={{ color: '#0d5c2e' }}>Two-step verification is on</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {enrolledMethod === 'email'
            ? 'You receive a 6-digit code by email each time you sign in.'
            : 'You use a code from your authenticator app each time you sign in.'}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Button onClick={() => navigate('/')} variant="contained" fullWidth sx={PRIMARY_BTN_SX}>Done</Button>
        <Button
          onClick={() => { setStage('choose'); setError(''); setInfo(''); setCode(''); }}
          variant="outlined"
          fullWidth
          sx={{ py: 1.1, borderRadius: '10px', fontWeight: 700, textTransform: 'none', borderWidth: '2px', borderColor: '#198a4b', color: '#198a4b', '&:hover': { borderWidth: '2px', borderColor: '#ef7d00', color: '#ef7d00', background: 'rgba(239,125,0,0.06)' } }}
        >
          Change verification method
        </Button>
        {!enforced && (
          <Button onClick={beginDisable} variant="outlined" fullWidth
            sx={{ py: 1.1, borderRadius: '10px', fontWeight: 700, textTransform: 'none', borderWidth: '2px', borderColor: '#e2e8f0', color: '#b91c1c', '&:hover': { borderWidth: '2px', borderColor: '#b91c1c', background: 'rgba(185,28,28,0.05)' } }}>
            Turn off two-step verification
          </Button>
        )}
        {enforced && (
          <Typography variant="caption" sx={{ color: '#94a3b8', textAlign: 'center' }}>
            Your role requires two-step verification, so it can&apos;t be turned off — but you can switch method above.
          </Typography>
        )}
      </Box>
    </Shell>
  );
};

export default MfaSetupPage;
